import { mutatePurchaseOrders } from "@/api/purchaseOrder/usePurchaseOrder";
import useWorksMutation from "@/api/works/useWorksMutation";
import { DateText, StyledTable, Wrapper } from "@/features/transportation/form/statementForm";
import customAlert from "@/features/ui/alert/alert";
import { LocationAutoComplete } from "@/features/ui/autoComplete/location/location-autoComplate";
import styled from "@emotion/styled";
import { Button, Flex, Text } from "@mantine/core";
import {
  PurchaseOrdersGet200ResponseRowsInner,
  PurchaseOrdersGet200ResponseRowsInnerInvoiceInner,
  ScmApiPurchaseOrdersPurchaseOrderIdPutRequest,
  WorksItemGet200ResponseRowsInner,
  WorksSlittingScrapDetailPost200ResponseInner,
} from "@sizlcorp/sizl-api-document/dist/models";
import { IconPrinter } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useState } from "react";
import { QRCodeComponent } from "../qrCode";

type PageType = "outSourcing" | "order" | "slitting";

type DataType<T extends PageType> = T extends "outSourcing" | "slitting"
  ? WorksItemGet200ResponseRowsInner
  : PurchaseOrdersGet200ResponseRowsInner | undefined;
interface OrderStatementFormProps<T extends PageType> {
  data: DataType<T>;
  pageType?: PageType;
  slittingData?: WorksSlittingScrapDetailPost200ResponseInner | undefined;
}

export const InvoiceStatementForm = <T extends PageType>(params: OrderStatementFormProps<T>) => {
  const { data, slittingData, pageType } = params;

  const { mutate } = useWorksMutation("update");
  const { mutate: orderUpdate } = useMutation(
    (params: ScmApiPurchaseOrdersPurchaseOrderIdPutRequest) =>
      mutatePurchaseOrders
        .update(params)
        .mutationFn(params as ScmApiPurchaseOrdersPurchaseOrderIdPutRequest | any),
    {
      onSuccess: () => {
        customAlert("납품창고가 변경되었습니다.", "창고변경", "green");
      },
      onError: (error) => {
        // Handle error
      },
    }
  );

  const [locationCode, setLocationCode] = useState<string | null>(
    (data as WorksItemGet200ResponseRowsInner)?.invoiceLocationCode ??
      (data as PurchaseOrdersGet200ResponseRowsInner)?.invoiceLocationCode ??
      null
  );

  const invoices =
    pageType === "outSourcing" || pageType === "slitting"
      ? ((data as WorksItemGet200ResponseRowsInner)?.invoice ?? [])
      : ((data as PurchaseOrdersGet200ResponseRowsInner)?.invoice ?? []);

  const transformedInvoices = invoices.map((invoice) => ({
    itemName: invoice.itemName,
    itemSpec: invoice.itemSpec,
    itemUnitText: invoice.itemUnitText,
    quantity: invoice.quantity,
    lotName: pageType === "outSourcing" ? undefined : invoice.lotName,
    width: (invoice as PurchaseOrdersGet200ResponseRowsInnerInvoiceInner).width,
  }));

  const workId = data && (data as WorksItemGet200ResponseRowsInner)?.id;
  const purchaseOrderId = data && (data as PurchaseOrdersGet200ResponseRowsInner)?.id;
  const invoiceUnitId =
    data && (data as PurchaseOrdersGet200ResponseRowsInner)?.invoice?.[0]?.invoiceUnitId;
  const slittingCode = data && (data as WorksItemGet200ResponseRowsInner)?.outsourceGroupCode;

  const today =
    new Date().getFullYear() +
    "년 " +
    (new Date().getMonth() + 1) +
    "월 " +
    new Date().getDate() +
    "일";

  const barcode =
    pageType === "outSourcing"
      ? `work_${workId}_${locationCode}`
      : pageType === "slitting"
        ? `slitting_${slittingCode}_${locationCode}`
        : `purchase_${purchaseOrderId}_${locationCode}_${invoiceUnitId}`;

  const downloadPDF = () => {
    const input = document.getElementById("pdf-content");
    if (input) {
      html2canvas(input, { scale: 2 }).then((canvas) => {
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF({
          orientation: "portrait",
          unit: "mm",
          format: "a4",
        });
        const imgProps = pdf.getImageProperties(imgData);
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const margin = 10;
        const maxWidth = pageWidth - margin * 2;
        const maxHeight = pageHeight - margin * 2;

        let imageWidth = maxWidth;
        let imageHeight = (imgProps.height * imageWidth) / imgProps.width;

        if (imageHeight > maxHeight) {
          imageHeight = maxHeight;
          imageWidth = (imgProps.width * imageHeight) / imgProps.height;
        }

        pdf.addImage(imgData, "PNG", margin, margin, imageWidth, imageHeight);
        pdf.save(`거래명세서.pdf`);
      });
    }
  };

  return (
    <>
      <Wrapper>
        <div id="pdf-content">
          <Flex justify="space-between" align="center">
            <Text size="3rem">MIRETECH</Text>
            <Flex align="center" gap="sm">
              <Text size="3rem" ta="center">
                거 래 명 세 표
              </Text>
              {locationCode && <QRCodeComponent barcode={barcode} />}
            </Flex>
            <div style={{ width: "6rem" }} />
          </Flex>
          <Flex justify={"space-between"} align="center" mt="md">
            <LocationAutoComplete
              label="납품창고"
              placeholder="납품창고를 선택해주세요."
              defaultValue={
                (data as WorksItemGet200ResponseRowsInner)?.invoiceLocationCode ??
                (data as PurchaseOrdersGet200ResponseRowsInner)?.invoiceLocationCode
              }
              onChange={(e) => {
                setLocationCode(e);
                pageType === "outSourcing" || pageType === "slitting"
                  ? mutate(
                      {
                        workId: (data as WorksItemGet200ResponseRowsInner)?.id ?? 0,
                        worksWorkIdDeleteRequest: {
                          invoiceLocationCode: e == null ? "" : e,
                        },
                        productionPlanId:
                          (data as WorksItemGet200ResponseRowsInner)?.productionPlanId ?? 0,
                        productionPlansProductionPlanIdDeleteRequest: {},
                      },
                      {
                        onSuccess: () => {
                          customAlert("납품창고가 변경되었습니다.", "창고변경", "green");
                        },
                        onError: (error) => {
                          // Handle error
                        },
                      }
                    )
                  : orderUpdate({
                      purchaseOrderId: (data as PurchaseOrdersGet200ResponseRowsInner)?.id ?? 0,
                      purchaseOrdersGetRequest: {
                        invoiceLocationCode: e == null ? "" : e,
                        partnerCode:
                          (data as PurchaseOrdersGet200ResponseRowsInner)?.partnerCode ?? "",
                        direction: "OUTBOUND",
                        // code: (data as PurchaseOrdersGet200ResponseRowsInner)?.code ?? "",
                      },
                    });
              }}
            />
            <Button
              onClick={downloadPDF}
              leftIcon={<IconPrinter />}
              disabled={!locationCode}
              color="teal"
              className="no-print"
            >
              출력
            </Button>
          </Flex>
          <Flex justify={"flex-end"} mt="auto">
            <DateText>{today}</DateText>
          </Flex>
          <StyledTable withBorder withColumnBorders w="75rem">
            <thead>
              <tr>
                <Th colSpan={4}>청구처</Th>
                <Th colSpan={4}>공급자</Th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <ContentTd>상호</ContentTd>
                <ContentTd colSpan={3}>미래테크 (주)</ContentTd>
                <ContentTd colSpan={4} rowSpan={4}></ContentTd>
              </tr>
              <tr>
                <ContentTd>사업자등록번호</ContentTd>
                <ContentTd>504-81-29089</ContentTd>
                <ContentTd>대표</ContentTd>
                <ContentTd>박해대,서순옥</ContentTd>
              </tr>
              <tr>
                <ContentTd>주소</ContentTd>
                <ContentTd colSpan={3}>대구광역시 북구 검단로 177-71(검단동)</ContentTd>
              </tr>
              <tr>
                <ContentTd>전화번호</ContentTd>
                <ContentTd>053-380-3400</ContentTd>
                <ContentTd>FAX</ContentTd>
                <ContentTd>053-383-7288</ContentTd>
              </tr>
            </tbody>
          </StyledTable>
          <StyledTable withBorder withColumnBorders w="100%">
            <thead>
              <tr>
                <th>금액</th>
                <th>품명</th>
                <th>규격</th>
                <th>단위</th>
                <th>단가</th>
                <th>수량</th>
                <th>LOT NO</th>
                <th>두께</th>
                <th>폭</th>
                <th>비고</th>
              </tr>
            </thead>
            <tbody>
              {transformedInvoices.length > 0 ? (
                transformedInvoices?.map((invoice) => {
                  return (
                    <tr key={invoice.itemName}>
                      <ContentTd></ContentTd>
                      <ContentTd>{invoice.itemName}</ContentTd>
                      <ContentTd>{invoice.itemSpec}</ContentTd>
                      <ContentTd>{invoice.itemUnitText}</ContentTd>
                      <ContentTd></ContentTd>
                      <ContentTd>{invoice.quantity}</ContentTd>
                      <ContentTd>{invoice.lotName}</ContentTd>
                      <ContentTd></ContentTd>
                      <ContentTd>{invoice.width}</ContentTd>
                      <ContentTd></ContentTd>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <ContentTd colSpan={10}>로트가 없습니다.</ContentTd>
                </tr>
              )}
            </tbody>
          </StyledTable>
        </div>
      </Wrapper>
    </>
  );
};

export const ContentTd = styled.td`
  padding: 10px;
  height: 3rem;
  text-align: center;
  font-size: 12px;
  font-weight: 400;
  color: #000;
  background-color: #fff;
`;

const Th = styled.th`
  padding: 10px;
  text-align: center;
  font-size: 12px;
  font-weight: 400;
  color: #000;
  background-color: #fff;
  width: 33%;
`;
